import React, { useState, useEffect } from 'react';
import {
  collection, doc, getDoc, updateDoc,
  addDoc, serverTimestamp, query, where, getDocs
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { VIDEO_BASE_URL } from '../../config/api';
import { uploadImageToBunny, deleteImageFromBunny, uploadVideoToBunny } from '../../utils/bunnyUpload';
import { sendNotification } from '../../utils/notificationService';
import {
  Trash2, Loader2, AlertCircle, Plus, X, Minus, Download, ArrowRight,
  GraduationCap, ChevronDown, ChevronUp, Globe, Lock, Upload, Link as LinkIcon, FileText, GripVertical
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../../context/ToastContext';
const LEVELS = { beginner: 'مبتدئ', intermediate: 'متوسط', advanced: 'متقدم' };
const STATUSES = { draft: 'مسودة', published: 'منشورة' };
const INITIAL_CATEGORIES = [
  'تصميم جرافيك',
  'موشن جرافيك',
  'تصميم مواقع',
  'ذكاء اصطناعي',
  'هويات بصرية',
  'تصميم واجهات',
  'تسويق'
];

/* ─── helpers ──────────────────────────────────────────────────── */
const slugify = (str) =>
  str.toLowerCase().trim()
    .replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/^-+|-+$/g, '');

const formatBytes = (bytes) => {
  if (!bytes) return '';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const blankLesson = () => ({
  type: 'video',
  title: '', description: '', videoFilename: '', durationMin: 0, durationSec: 0,
  accessLevel: 'subscriber', thumbnailUrl: '',
  textContent: '',
  quizData: { questions: [], isMandatory: false, allowRetakes: true, cooldownHours: 0, passingScore: 60 },
});
const blankQuestion = () => ({ question: '', options: ['', '', '', ''], correctIndex: 0 });
const blankUnit = () => ({
  title: '', lessons: [blankLesson()],
});

const getLessonTheme = (type) => {
  switch (type) {
    case 'video': return {
      activeBtn: 'bg-blue-600 text-white shadow-md shadow-blue-500/20 ring-1 ring-blue-500/50',
      numberBox: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/20',
      containerExpanded: 'bg-blue-50/30 dark:bg-blue-900/10 border-blue-200 dark:border-blue-500/30'
    };
    case 'text': return {
      activeBtn: 'bg-amber-500 text-white shadow-md shadow-amber-500/20 ring-1 ring-amber-500/50',
      numberBox: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20',
      containerExpanded: 'bg-amber-50/30 dark:bg-amber-900/10 border-amber-200 dark:border-amber-500/30'
    };
    case 'quiz': return {
      activeBtn: 'bg-purple-600 text-white shadow-md shadow-purple-500/20 ring-1 ring-purple-500/50',
      numberBox: 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-500/20',
      containerExpanded: 'bg-purple-50/30 dark:bg-purple-900/10 border-purple-200 dark:border-purple-500/30'
    };
    default: return {
      activeBtn: 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200 dark:ring-white/10',
      numberBox: 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-zinc-400 border-slate-200 dark:border-white/10',
      containerExpanded: 'bg-slate-50 dark:bg-zinc-800/30 border-slate-300 dark:border-white/20'
    };
  }
};

const emptyForm = {
  title: '', titleEn: '', slug: '',
  shortDesc: '', longDesc: '',
  whatYouLearn: [''],       // array of strings (bullet points)
  requirements: [''],       // array of strings (bullet points)
  level: 'beginner',
  category: '',             // NEW
  price: 0, status: 'draft', thumbnail: '', courseVideoId: '', instructorId: '',
  hasCertificate: false, lifetimeAccess: true,
  attachments: [{ groupName: 'المجموعة الأولى', files: [] }],
  curriculum: [blankUnit()],
};

export default function AdminCourseEditor() {
  const { id: courseId } = useParams();
  const isEditMode = !!courseId;
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(!!courseId);
  const [isInstructorOpen, setIsInstructorOpen] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [lessonUploadingKey, setLessonUploadingKey] = useState(null);
  const [allCategories, setAllCategories] = useState(INITIAL_CATEGORIES);
  const [editingCourse, setEditingCourse] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [fetchingMeta, setFetchingMeta] = useState(null);
  const [expandedUnitIdx, setExpandedUnitIdx] = useState(0);
  const [expandedLessonIdx, setExpandedLessonIdx] = useState(0);
  const [dragOverUnitIdx, setDragOverUnitIdx] = useState(null);
  const [dragOverLesson, setDragOverLesson] = useState(null);
  const navigate = useNavigate();
  const { addToast } = useToast();

  // ─── Fetch course for edit mode ─────────────────────────────────
  useEffect(() => {
    if (!courseId) { setLoading(false); return; }
    const fetchCourse = async () => {
      try {
        const snap = await getDoc(doc(db, 'courses', courseId));
        if (!snap.exists()) { addToast('الدورة غير موجودة', 'error'); navigate('/admin/courses'); return; }
        const course = { id: snap.id, ...snap.data() };
        setEditingCourse(course);
        let migratedAtts = course.attachments || [];
        if (migratedAtts.length > 0 && migratedAtts[0].fileUrl) migratedAtts = [{ groupName: 'ملحقات عامة', files: migratedAtts }];
        if (migratedAtts.length === 0) migratedAtts = [{ groupName: 'المجموعة الأولى', files: [] }];
        setForm({
          title: course.title || '', titleEn: course.titleEn || '', slug: course.slug || '',
          shortDesc: course.shortDesc || course.description || '', longDesc: course.longDesc || '',
          whatYouLearn: course.whatYouLearn?.length ? course.whatYouLearn : [''],
          requirements: course.requirements?.length ? course.requirements : [''],
          level: course.level || 'beginner', category: course.category || '',
          price: course.price || 0, status: course.status || 'draft',
          thumbnail: course.thumbnail || '', courseVideoId: course.courseVideoId || '',
          instructorId: course.instructorId || '',
          hasCertificate: course.hasCertificate || false,
          lifetimeAccess: course.lifetimeAccess !== undefined ? course.lifetimeAccess : true,
          attachments: migratedAtts,
          curriculum: course.curriculum?.length ? course.curriculum : [blankUnit()],
        });
      } catch (err) { console.error(err); addToast('خطأ في تحميل الدورة', 'error'); }
      finally { setLoading(false); }
    };
    fetchCourse();
  }, [courseId]);

  // ─── Fetch categories from existing courses ─────────────────────
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const snap = await getDocs(collection(db, 'courses'));
        const uniqueCats = [...new Set(snap.docs.map(d => d.data().category).filter(Boolean))];
        setAllCategories([...new Set([...INITIAL_CATEGORIES, ...uniqueCats])]);
      } catch (e) { /* ignore */ }
    };
    fetchCats();
  }, []);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const q = query(collection(db, 'users'), where('role', '==', 'instructor'), where('instructorData.status', '==', 'approved'));
        const snap = await getDocs(q);
        setInstructors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
    };
    fetchInstructors();
  }, []);

  // ─── helpers to calc totals ───────────────────────────────────────
  const calcTotals = (curriculum) => {
    let totalLessons = 0, totalSeconds = 0;
    (curriculum || []).forEach(u => {
      (u.lessons || []).forEach(l => {
        totalLessons++;
        if (l.type === 'text') {
          totalSeconds += 3 * 60;
        } else if (l.type === 'quiz') {
          totalSeconds += 4 * 60;
        } else if (l.type === 'assignment' || l.type === 'final_project') {
          totalSeconds += 60 * 60; // 60 minutes
        } else {
          totalSeconds += (parseInt(l.durationMin) || 0) * 60 + (parseInt(l.durationSec) || 0);
        }
      });
    });
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    return { totalUnits: (curriculum || []).length, totalLessons, totalSeconds, durationLabel: hrs > 0 ? `${hrs} ساعة ${mins} دقيقة` : `${mins} دقيقة` };
  };

  // ─── Attachments Handlers ─────────────────────────────────────────
  const addAttachmentGroup = () => {
    setForm(f => ({ ...f, attachments: [...(f.attachments || []), { groupName: 'مجموعة جديدة', files: [] }] }));
  };

  const removeAttachmentGroup = (gi) => {
    const newAtts = [...form.attachments];
    newAtts.splice(gi, 1);
    setForm(f => ({ ...f, attachments: newAtts }));
  };

  const addAttachmentLink = (gi) => {
    const newAtts = [...form.attachments];
    newAtts[gi].files.push({ fileName: '', fileUrl: '', fileType: 'link' });
    setForm({ ...form, attachments: newAtts });
  };

  const handleFileUpload = (e, gi) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 20 * 1024 * 1024) return alert('حجم الملف كبير جداً. الحد الأقصى 20 ميغابايت.');

    setIsSaving(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await fetch(`${VIDEO_BASE_URL}/api/course/upload-attachment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileBase64: reader.result,
            originalName: file.name,
            fileType: file.type || 'application/octet-stream'
          })
        });
        const data = await res.json();
        if (data.success) {
          const newAtts = [...form.attachments];
          newAtts[gi].files.push({ fileName: data.fileName, fileUrl: data.fileUrl, fileType: data.fileType, fileSize: formatBytes(file.size) });
          setForm({ ...form, attachments: newAtts });
        } else alert('فشل الرفع: ' + data.error);
      } catch (err) {
        alert('حدث خطأ أثناء الرفع.');
      } finally {
        setIsSaving(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCourseVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 200 * 1024 * 1024) {
      alert('حجم الفيديو كبير جداً (الأقصى 200MB)');
      return;
    }

    setIsUploadingVideo(true);
    setVideoUploadProgress(0);

    try {
      const videoId = await uploadVideoToBunny(file, (progress) => {
        setVideoUploadProgress(progress);
      });
      setForm(prev => ({ ...prev, courseVideoId: videoId }));
    } catch (err) {
      console.error("Error uploading course video:", err);
      alert('حدث خطأ أثناء رفع الفيديو: ' + err.message);
    } finally {
      setIsUploadingVideo(false);
      setVideoUploadProgress(0);
    }
  };

  const handleLessonVideoUpload = async (ui, li, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const key = `${ui}-${li}`;
    setLessonUploadingKey(key);
    setVideoUploadProgress(0);

    try {
      const videoId = await uploadVideoToBunny(file, (progress) => {
        setVideoUploadProgress(progress);
      });
      updateLesson(ui, li, 'videoFilename', videoId);
      // Try to fetch duration automatically
      fetchLessonDuration(ui, li, videoId);
    } catch (err) {
      console.error("Error uploading lesson video:", err);
      alert('حدث خطأ أثناء رفع الدرس: ' + err.message);
    } finally {
      setLessonUploadingKey(null);
      setVideoUploadProgress(0);
    }
  };



  // ─── Save ─────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      addToast('يرجى إدخال عنوان الدورة.', 'warning');
      return;
    }
    if (!form.instructorId) {
      addToast('يرجى اختيار مدرب للدورة.', 'warning');
      return;
    }

    // --- Validation ---
    for (let ui = 0; ui < form.curriculum.length; ui++) {
      const unit = form.curriculum[ui];
      for (let li = 0; li < unit.lessons.length; li++) {
        const lesson = unit.lessons[li];
        const type = lesson.type || 'video';

        if (type === 'video' && !lesson.videoFilename?.trim() && !lesson.videoUrl?.trim()) {
          addToast(`الوحدة ${ui + 1} - الدرس ${li + 1}: يرجى إضافة رابط الفيديو.`, 'warning');
          return;
        }
        if (type === 'text' && !lesson.textContent?.trim()) {
          addToast(`الوحدة ${ui + 1} - الدرس ${li + 1}: يرجى إضافة محتوى المقال.`, 'warning');
          return;
        }
        if (type === 'quiz' && (!lesson.quizData?.questions || lesson.quizData.questions.length === 0)) {
          addToast(`الوحدة ${ui + 1} - الدرس ${li + 1}: يرجى إضافة سؤال واحد على الأقل للاختبار.`, 'warning');
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      const { totalUnits, totalLessons, totalSeconds, durationLabel } = calcTotals(form.curriculum);
      const filteredWYL = form.whatYouLearn.filter(s => s.trim());
      const filteredReqs = form.requirements.filter(s => s.trim());
      const cleanCurriculum = form.curriculum.map(u => ({
        ...u,
        title: u.title || '',
        lessons: u.lessons.map(l => {
          const type = l.type || 'video';
          const baseLesson = {
            ...l, // preserve incoming metadata (id, etc)
            id: l.id || crypto.randomUUID(),
            type,
            title: l.title || '',
            description: l.description || '',
            accessLevel: l.accessLevel || 'subscriber',
            thumbnailUrl: l.thumbnailUrl || '',
          };

          if (type === 'video') {
            return {
              ...baseLesson,
              videoFilename: l.videoFilename || l.videoUrl || '', // Fallback to avoid undefined
              durationMin: parseInt(l.durationMin) || 0,
              durationSec: parseInt(l.durationSec) || 0,
            };
          } else if (type === 'text') {
            return {
              ...baseLesson,
              textContent: l.textContent || '',
              durationMin: 3,
              durationSec: 0,
            };
          } else if (type === 'quiz') {
            return {
              ...baseLesson,
              quizData: l.quizData || { questions: [], isMandatory: false, allowRetakes: true, cooldownHours: 0, passingScore: 60 },
              durationMin: 4,
              durationSec: 0,
            };
          } else if (type === 'assignment' || type === 'final_project') {
            return {
              ...baseLesson,
              durationMin: 60,
              durationSec: 0,
              maxScore: 100,
            };
          }
          return baseLesson;
        }),
      }));

      const payload = {
        title: form.title.trim(),
        titleEn: form.titleEn.trim(),
        slug: form.slug || slugify(form.titleEn || form.title),
        description: form.shortDesc.trim(),         // backward compat
        shortDesc: form.shortDesc.trim(),
        longDesc: form.longDesc.trim(),
        whatYouLearn: filteredWYL,
        requirements: filteredReqs,
        level: form.level,
        category: form.category || '',
        price: parseFloat(form.price) || 0,
        status: form.status,
        thumbnail: form.thumbnail.trim(),
        courseVideoId: form.courseVideoId?.trim() || '',
        instructorId: form.instructorId,
        hasCertificate: form.hasCertificate,
        lifetimeAccess: form.lifetimeAccess,
        attachments: form.attachments || [],
        curriculum: cleanCurriculum,
        totalUnits, totalLessons, totalSeconds, durationLabel,
      };
      if (editingCourse) {
        await updateDoc(doc(db, 'courses', editingCourse.id), payload);
        try {
           const regQ = query(collection(db, 'course_registrations'), where('courseId', '==', editingCourse.id), where('status', '==', 'approved'));
           const snapRegs = await getDocs(regQ);
           const notificationPromises = snapRegs.docs.map(studentDoc => {
             const sData = studentDoc.data();
             return sendNotification(sData.studentId, 'تحديث في محتوى الدورة', 'تم تحديث أو إضافة دروس جديدة في دورتك: ' + payload.title, 'success', '/dashboard/courses');
           });
           await Promise.all(notificationPromises);
        } catch(e) { console.error('Failed to notify students', e); }
        addToast('تم تحديث الدورة بنجاح!', 'success');
      } else {
        await addDoc(collection(db, 'courses'), { ...payload, studentsCount: 0, createdAt: serverTimestamp() });
        addToast('تم إنشاء الدورة بنجاح!', 'success');
      }
      navigate('/admin/courses');
    } catch (err) {
      console.error(err);
      addToast('حدث خطأ أثناء الحفظ.', 'error');
    } finally {
      setIsSaving(false);
    }
  };



  /* ── Curriculum array helpers ─────────────────── */
  /* ── Curriculum array helpers ─────────────────── */
  const addUnit = () => {
    setForm(f => ({ ...f, curriculum: [...f.curriculum, blankUnit()] }));
    setExpandedUnitIdx(form.curriculum.length); // Open the newly added unit at the bottom
  };
  const removeUnit = (ui) => {
    setForm(f => ({ ...f, curriculum: f.curriculum.filter((_, i) => i !== ui) }));
    if (expandedUnitIdx === ui) setExpandedUnitIdx(Math.max(0, ui - 1));
    else if (expandedUnitIdx > ui) setExpandedUnitIdx(expandedUnitIdx - 1);
  };
  const updateUnitTitle = (ui, val) => {
    setForm(f => {
      const c = [...f.curriculum];
      c[ui] = { ...c[ui], title: val };
      return { ...f, curriculum: c };
    });
  };
  const addLesson = (ui) => {
    setForm(f => {
      const c = [...f.curriculum];
      c[ui] = { ...c[ui], lessons: [...c[ui].lessons, blankLesson()] };
      return { ...f, curriculum: c };
    });
    setExpandedLessonIdx(form.curriculum[ui].lessons.length);
  };
  const removeLesson = (ui, li) => {
    setForm(f => {
      const c = [...f.curriculum];
      c[ui] = { ...c[ui], lessons: c[ui].lessons.filter((_, i) => i !== li) };
      return { ...f, curriculum: c };
    });
  };
  const updateLesson = (ui, li, field, val) => {
    setForm(f => {
      const c = [...f.curriculum];
      c[ui] = { ...c[ui], lessons: c[ui].lessons.map((l, i) => i === li ? { ...l, [field]: val } : l) };
      return { ...f, curriculum: c };
    });
  };

  const moveUnit = (fromIdx, toIdx) => {
    setForm(f => {
      const c = [...f.curriculum];
      const items = c.splice(fromIdx, 1);
      c.splice(toIdx, 0, items[0]);
      return { ...f, curriculum: c };
    });
  };

  const moveLesson = (ui, fromIdx, toIdx) => {
    setForm(f => {
      const c = [...f.curriculum];
      const lessons = [...c[ui].lessons];
      const items = lessons.splice(fromIdx, 1);
      lessons.splice(toIdx, 0, items[0]);
      c[ui] = { ...c[ui], lessons };
      return { ...f, curriculum: c };
    });
  };

  /* ── Upload main course thumbnail to Bunny Storage via backend ────── */
  const handleCourseThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFetchingMeta('course-thumb');

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64data = reader.result.split(',')[1];
        try {
          const res = await fetch(`${VIDEO_BASE_URL}/api/video/upload-thumbnail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageBase64: base64data })
          });

          if (!res.ok) throw new Error('Failed to upload thumbnail');
          const data = await res.json();

          if (data.success && data.thumbnailUrl) {
            setForm(f => ({ ...f, thumbnail: data.thumbnailUrl }));
          } else {
            alert(data.error || 'فشل في رفع الصورة');
          }
        } catch (err) {
          console.error(err);
          alert('حدث خطأ أثناء الرفع للسيرفر.');
        } finally {
          setFetchingMeta(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error(e);
      setFetchingMeta(null);
    }
  };

  /* ── Upload lesson thumbnail to Bunny Storage via backend ────── */
  const handleThumbnailUpload = async (ui, li, file) => {
    if (!file) return;
    const key = `${ui}-${li}`;
    setFetchingMeta(key); // reuse loading state

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64data = reader.result.split(',')[1];

        try {
          const res = await fetch(`${VIDEO_BASE_URL}/api/video/upload-thumbnail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageBase64: base64data })
          });

          if (!res.ok) throw new Error('Failed to upload thumbnail');
          const data = await res.json();

          if (data.success && data.thumbnailUrl) {
            updateLesson(ui, li, 'thumbnailUrl', data.thumbnailUrl);
          } else {
            alert(data.error || 'فشل في رفع الصورة');
          }
        } catch (err) {
          console.error(err);
          alert('حدث خطأ أثناء الرفع للسيرفر.');
        } finally {
          setFetchingMeta(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error(e);
      setFetchingMeta(null);
    }
  };

  /* ── Auto-Fetch duration from Bunny CDN ──── */
  const fetchLessonDuration = async (ui, li, videoId) => {
    if (!videoId) return;

    const key = `dur-${ui}-${li}`;
    setFetchingMeta(key);

    try {
      if (!auth.currentUser) return;
      const token = await auth.currentUser.getIdToken();

      const res = await fetch(`${VIDEO_BASE_URL}/api/video/bunny-meta?videoId=${encodeURIComponent(videoId)}&token=${encodeURIComponent(token)}`);
      const data = await res.json();

      if (!data.success) throw new Error(data.error);

      const totalSeconds = Math.round(data.length || 0);
      const m = Math.floor(totalSeconds / 60);
      const s = totalSeconds % 60;

      updateLesson(ui, li, 'durationMin', m);
      updateLesson(ui, li, 'durationSec', s);
    } catch (err) {
      console.error('Auto-fetch duration failed:', err);
    } finally {
      setFetchingMeta(null);
    }
  };

  /* ── Bullet list helpers ─────────────────────── */
  const addBullet = (field) => setForm(f => ({ ...f, [field]: [...f[field], ''] }));
  const removeBullet = (field, idx) => setForm(f => ({ ...f, [field]: f[field].filter((_, i) => i !== idx) }));
  const updateBullet = (field, idx, val) => setForm(f => ({ ...f, [field]: f[field].map((s, i) => i === idx ? val : s) }));

  // ───── Shared input class ─────
  const inputCls = "w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-medium text-slate-900 dark:text-white transition-all text-md";

  // ─── UI ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-slate-500">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
        <p className="font-bold">جاري تحميل بيانات الدورة...</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" dir="rtl">

      {/* ── Page Header ── */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/courses" className="w-11 h-11 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 dark:text-zinc-400 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors shrink-0">
            <ArrowRight className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              {isEditMode ? 'تعديل الدورة' : 'إنشاء دورة جديدة'}
            </h1>
            <p className="text-md font-medium text-slate-500 dark:text-zinc-400 mt-0.5">
              {isEditMode ? `تعديل: ${form.title || '...'}` : 'قم بإعداد متطلبات الدورة ومحتواها.'}
            </p>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl shadow-sm overflow-hidden">
        <div className="flex px-4 border-b border-slate-100 dark:border-white/5 sticky top-0 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl z-10">
          {[
            { key: 'basic', label: 'الأساسيات', emoji: '⚙️' },
            { key: 'content', label: 'المحتوى', emoji: '📋' },
            { key: 'curriculum', label: 'المنهج والدروس', emoji: '📚' },
          ].map(t => (
            <button key={t.key} type="button" onClick={(e) => { e.preventDefault(); setActiveTab(t.key); }}
              className={`flex-1 py-4 text-base font-bold transition-all border-b-2 px-2 flex items-center justify-center gap-2 ${activeTab === t.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300'
                }`}>
              <span>{t.emoji}</span> {t.label}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="p-6 md:p-10 space-y-6">

          {/* TAB: BASIC ───────────────────── */}
          {activeTab === 'basic' && (<div className="space-y-6 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">عنوان الدورة (عربي) <span className="text-red-500">*</span></label>
                      <input type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="دورة تصميم الجرافيك" className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">عنوان الدورة (إنجليزي) → الرابط</label>
                      <input type="text" value={form.titleEn} onChange={e => setForm({ ...form, titleEn: e.target.value, slug: slugify(e.target.value) })} placeholder="Graphic Design Course" className={inputCls} dir="ltr" />
                      {form.slug && <p className="text-md text-primary-500 mt-1.5 font-mono font-bold" dir="ltr">/{form.slug}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-3">مدرب الدورة <span className="text-red-500">*</span></label>

                    <div className="relative">
                      {/* Selected Instructor Display */}
                      <button
                        type="button"
                        onClick={() => setIsInstructorOpen(!isInstructorOpen)}
                        className="w-full flex items-center gap-4 px-5 py-3 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl hover:border-primary-500 transition-all text-right shadow-sm"
                      >
                        {form.instructorId ? (
                          <>
                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary-500/20 shrink-0">
                              <img
                                src={instructors.find(i => i.id === form.instructorId)?.photoURL || 'https://ui-avatars.com/api/?name=' + instructors.find(i => i.id === form.instructorId)?.name}
                                alt="Instructor"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <p className="font-bold text-slate-900 dark:text-white">{instructors.find(i => i.id === form.instructorId)?.name}</p>
                              <p className="text-xs text-slate-500 dark:text-zinc-500">{instructors.find(i => i.id === form.instructorId)?.instructorData?.specialization || 'مدرب معتمد'}</p>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isInstructorOpen ? 'rotate-180 text-primary-500' : ''}`} />
                          </>
                        ) : (
                          <div className="flex items-center justify-between w-full">
                            <span className="text-slate-400 font-bold">-- اختر مدرب الدورة --</span>
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                      </button>

                      {/* Dropdown List */}
                      <AnimatePresence>
                        {isInstructorOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="absolute top-full right-0 left-0 mt-3 p-2 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-2xl shadow-2xl z-[60] max-h-[350px] overflow-y-auto custom-scrollbar"
                          >
                            {instructors.length === 0 ? (
                              <p className="p-4 text-center text-slate-500">لا يوجد مدربين متاحين</p>
                            ) : (
                              instructors.map(inst => (
                                <button
                                  key={inst.id}
                                  type="button"
                                  onClick={() => {
                                    setForm({ ...form, instructorId: inst.id });
                                    setIsInstructorOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all mb-1 ${form.instructorId === inst.id
                                    ? 'bg-primary-500 text-white'
                                    : 'hover:bg-slate-100 dark:hover:bg-white/5 text-slate-700 dark:text-zinc-400'
                                    }`}
                                >
                                  <div className={`w-10 h-10 rounded-full overflow-hidden border ${form.instructorId === inst.id ? 'border-white/30' : 'border-slate-200 dark:border-white/10'}`}>
                                    <img
                                      src={inst.photoURL || 'https://ui-avatars.com/api/?name=' + inst.name}
                                      alt={inst.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-sm">{inst.name}</p>
                                    <p className={`text-xs ${form.instructorId === inst.id ? 'text-primary-100' : 'text-slate-500'}`}>
                                      {inst.instructorData?.specialization || 'مدرب'}
                                    </p>
                                  </div>
                                </button>
                              ))
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Outside Click Guard */}
                      {isInstructorOpen && (
                        <div className="fixed inset-0 z-[50]" onClick={() => setIsInstructorOpen(false)} />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-3 flex items-center justify-between">
                      <span>فئة الدورة <span className="text-red-500">*</span></span>
                      {form.category && !allCategories.includes(form.category) && (
                        <span className="text-xs bg-primary-500/10 text-primary-600 px-2 py-0.5 rounded-full">فئة مخصصة</span>
                      )}
                    </label>

                    {/* Category Pills */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {allCategories.map(cat => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setForm({ ...form, category: cat });
                          }}
                          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all border ${form.category === cat && !showNewCategoryInput
                            ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/20'
                            : 'bg-white dark:bg-zinc-800/50 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-white/5 hover:border-primary-200 dark:hover:border-primary-500/10'
                            }`}
                        >
                          {cat}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCategoryInput(true);
                          setForm({ ...form, category: '' });
                        }}
                        className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all border ${showNewCategoryInput
                          ? 'bg-primary-600 text-white border-primary-500 shadow-md'
                          : 'bg-primary-500/10 text-primary-600 border-primary-500/20 hover:bg-primary-500/20'
                          }`}
                      >
                        + إضافة فئة جديدة
                      </button>
                    </div>

                    {/* Custom Entry Input */}
                    {showNewCategoryInput && (
                      <div className="animate-in slide-in-from-top-2 duration-300">
                        <input
                          type="text"
                          autoFocus
                          value={form.category}
                          onChange={e => setForm({ ...form, category: e.target.value })}
                          placeholder="اكتب اسم الفئة الجديدة..."
                          className={`${inputCls} w-full ring-2 ring-primary-500/20`}
                          required
                        />
                        <p className="text-xs text-slate-400 mt-2">ملاحظة: سيتم إضافة هذا القسم لقائمة الفلاتر فور حفظ الدورة.</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">وصف قصير (يظهر في البطاقة)</label>
                    <textarea value={form.shortDesc} onChange={e => setForm({ ...form, shortDesc: e.target.value })} rows={2} placeholder="ملخص الدورة في سطر أو سطرين..." className={`${inputCls} resize-none`} />
                  </div>

                  <div>
                    <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">الوصف التفصيلي</label>
                    <textarea value={form.longDesc} onChange={e => setForm({ ...form, longDesc: e.target.value })} rows={4} placeholder="وصف شامل عن الدورة وأهدافها..." className={`${inputCls} resize-y`} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-3">المستوى التعليمي</label>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(LEVELS).map(([v, l]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setForm({ ...form, level: v })}
                            className={`flex-1 px-4 py-3 rounded-xl font-bold text-sm transition-all border ${form.level === v
                              ? 'bg-primary-600 text-white border-primary-500 shadow-lg shadow-primary-500/20'
                              : 'bg-white dark:bg-zinc-800/50 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-white/5 hover:border-primary-200'
                              }`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">السعر ($)</label>
                      <input type="number" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className={inputCls} dir="ltr" />
                    </div>
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">حالة النشر</label>
                      <div className="flex bg-slate-100 dark:bg-black/30 p-1 rounded-xl">
                        {Object.entries(STATUSES).map(([v, l]) => (
                          <button key={v} type="button" onClick={() => setForm({ ...form, status: v })}
                            className={`flex-1 py-2 rounded-lg font-bold text-md transition-all ${form.status === v
                              ? (v === 'published' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-white dark:bg-zinc-700 text-slate-900 dark:text-white shadow-sm')
                              : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'
                              }`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">غلاف الدورة (الصورة المصغرة)</label>
                      <div className="flex gap-4 items-start">
                        {form.thumbnail && (
                          <div className="w-24 h-20 rounded-xl overflow-hidden shrink-0 shadow-sm border border-slate-200 dark:border-white/10 relative group">
                            <img src={form.thumbnail} alt="Cover" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1">
                          <label className={`flex flex-col items-center justify-center w-full h-20 border-2 ${fetchingMeta === 'course-thumb' ? 'border-primary-400 border-solid bg-primary-50 dark:bg-primary-900/20' : 'border-dashed border-slate-300 hover:border-primary-500 bg-slate-50 hover:bg-slate-100 dark:border-white/10 dark:bg-black/20 dark:hover:bg-white/5'} rounded-xl cursor-pointer transition-colors text-slate-500 dark:text-zinc-400`}>
                            {fetchingMeta === 'course-thumb' ? (
                              <div className="flex flex-col items-center">
                                <Loader2 className="w-6 h-6 text-primary-500 animate-spin mb-1" />
                                <span className="font-bold text-xs">جاري الرفع...</span>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1">
                                <Upload className="w-5 h-5 text-primary-400" />
                                <span className="font-bold text-xs">اختر صورة أو اسحبها هنا</span>
                              </div>
                            )}
                            <input type="file" className="hidden" accept="image/*" onChange={handleCourseThumbnailUpload} disabled={fetchingMeta === 'course-thumb'} />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">مقطع الإعلان (Course Intro Video)</label>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={form.courseVideoId || ''}
                            onChange={e => setForm({ ...form, courseVideoId: e.target.value.trim() })}
                            placeholder="Video ID (e.g. f8caa28a...)"
                            className={`${inputCls} flex-1`}
                            dir="ltr"
                            disabled={isUploadingVideo}
                          />
                          <input
                            type="file"
                            id="courseVideoFile"
                            accept="video/*"
                            className="hidden"
                            onChange={handleCourseVideoUpload}
                          />
                          <label
                            htmlFor="courseVideoFile"
                            className={`px-4 py-3 rounded-xl font-bold transition-all flex items-center gap-2 cursor-pointer border ${isUploadingVideo
                              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                              : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 border-primary-500/20 hover:bg-primary-500/20'
                              }`}
                          >
                            {isUploadingVideo ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <Upload className="w-5 h-5" />
                            )}
                            <span>رفع فيديو</span>
                          </label>
                        </div>

                        {isUploadingVideo && (
                          <div className="w-full h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden border border-slate-200 dark:border-white/10">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${videoUploadProgress}%` }}
                              className="h-full bg-primary-500"
                            />
                          </div>
                        )}

                        {isUploadingVideo && (
                          <p className="text-xs text-slate-500 dark:text-zinc-400 font-bold text-center">
                            جاري الرفع... {videoUploadProgress}%
                          </p>
                        )}

                        <p className="text-xs text-slate-400 italic">
                          يمكنك إدخال الـ ID يدوياً أو رفع ملف فيديو مباشرة ليتم معالجته في Bunny Stream.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <label className="flex items-center gap-3 bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-white/5 cursor-pointer hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                      <input type="checkbox" checked={form.hasCertificate} onChange={e => setForm({ ...form, hasCertificate: e.target.checked })} className="w-5 h-5 accent-primary-500" />
                      <span className="font-bold text-md text-slate-700 dark:text-zinc-200">يوجد شهادة إتمام</span>
                    </label>
                    <label className="flex items-center gap-3 bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-white/5 cursor-pointer hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
                      <input type="checkbox" checked={form.lifetimeAccess} onChange={e => setForm({ ...form, lifetimeAccess: e.target.checked })} className="w-5 h-5 accent-primary-500" />
                      <span className="font-bold text-md text-slate-700 dark:text-zinc-200">وصول مدى الحياة</span>
                    </label>
                  </div>
                </div>)}

                {/* TAB: CONTENT ─────────────────── */}
                {activeTab === 'content' && (<div className="space-y-8 animate-in fade-in duration-300">
                  {/* What you'll learn */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-lg font-bold text-slate-900 dark:text-white">ماذا سيتعلم الطالب؟</label>
                      <button type="button" onClick={() => addBullet('whatYouLearn')} className="text-md font-bold text-primary-600 hover:text-primary-700 dark:hover:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                        <Plus className="w-4 h-4" /> إضافة عنصر
                      </button>
                    </div>
                    <div className="space-y-3">
                      {form.whatYouLearn.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 group">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0">
                            <span className="font-extrabold text-md opacity-80">✓</span>
                          </div>
                          <input type="text" value={item} onChange={e => updateBullet('whatYouLearn', i, e.target.value)}
                            placeholder="ميزة أو مهارة يتعلمها الطالب..." className={inputCls} />
                          {form.whatYouLearn.length > 1 && (
                            <button type="button" onClick={() => removeBullet('whatYouLearn', i)} className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 text-red-500 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 dark:hover:bg-red-500/20">
                              <Minus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-lg font-bold text-slate-900 dark:text-white">متطلبات الدورة</label>
                      <button type="button" onClick={() => addBullet('requirements')} className="text-md font-bold text-primary-600 hover:text-primary-700 dark:hover:text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1">
                        <Plus className="w-4 h-4" /> إضافة عنصر
                      </button>
                    </div>
                    <div className="space-y-3">
                      {form.requirements.map((item, i) => (
                        <div key={i} className="flex items-center gap-3 group">
                          <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-white/40 flex items-center justify-center shrink-0">
                            <span className="font-extrabold text-md opacity-80">•</span>
                          </div>
                          <input type="text" value={item} onChange={e => updateBullet('requirements', i, e.target.value)}
                            placeholder="متطلب للبدء بالدورة..." className={inputCls} />
                          {form.requirements.length > 1 && (
                            <button type="button" onClick={() => removeBullet('requirements', i)} className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 text-red-500 flex items-center justify-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 dark:hover:bg-red-500/20">
                              <Minus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Attachments */}
                  <div className="pt-6 border-t border-slate-100 dark:border-white/5">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <label className="text-lg font-bold text-slate-900 dark:text-white">ملحقات الدورة</label>
                        <p className="text-md text-slate-500 dark:text-zinc-500 mt-1">يتم التنظيم في مجموعات (مثال: ملحقات الوحدة الأولى)</p>
                      </div>
                      <button type="button" onClick={addAttachmentGroup} className="text-md font-bold text-slate-700 hover:text-slate-900 dark:text-white bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 px-4 py-2 rounded-xl transition-colors flex items-center gap-1">
                        <Plus className="w-4 h-4" /> إضافة مجموعة
                      </button>
                    </div>

                    <div className="space-y-6">
                      {(form.attachments || []).map((group, gi) => (
                        <div key={gi} className="bg-slate-50/50 dark:bg-white/5 border border-slate-200 dark:border-white/5 rounded-3xl p-5 shadow-sm">
                          <div className="flex items-center gap-3 mb-5">
                            <input type="text" value={group.groupName} onChange={e => { const newA = [...form.attachments]; newA[gi].groupName = e.target.value; setForm({ ...form, attachments: newA }); }} placeholder="اسم المجموعة..." className={`${inputCls} !text-lg !font-black !py-2.5 flex-1 shadow-sm`} />
                            <button type="button" onClick={() => removeAttachmentGroup(gi)} className="p-3 text-red-400 hover:text-red-600 bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 rounded-xl transition-colors shrink-0">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          <div className="flex items-center gap-3 mb-4 border-b border-slate-200 dark:border-white/5 pb-4">
                            <button type="button" onClick={() => addAttachmentLink(gi)} className="text-md font-bold bg-white dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 px-4 py-2 rounded-xl flex items-center gap-2 cursor-pointer transition-colors shadow-sm">
                              <Link className="w-4 h-4" /> إضافة رابط
                            </button>
                            <label className={`text-md font-bold bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-500/20 hover:border-primary-300 dark:hover:border-primary-500/40 px-4 py-2 rounded-xl cursor-pointer flex items-center gap-2 transition-colors shadow-sm ${isSaving ? 'opacity-50 pointer-events-none' : ''}`}>
                              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} رفع ملف من الجهاز
                              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.zip,.rar,.png,.jpg,.jpeg" onChange={(e) => handleFileUpload(e, gi)} disabled={isSaving} />
                            </label>
                          </div>

                          <div className="space-y-3">
                            {group.files?.map((att, fi) => (
                              <div key={fi} className="flex flex-col md:flex-row md:items-center gap-4 bg-white dark:bg-black/30 border border-slate-200 dark:border-white/5 rounded-2xl p-3 shadow-sm">
                                {att.fileType === 'link' ? (
                                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                                    <Link className="w-5 h-5 text-blue-500" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-500/10 flex items-center justify-center shrink-0">
                                    <FileText className="w-5 h-5 text-rose-500" />
                                  </div>
                                )}

                                <div className="flex-1 min-w-0 flex flex-col md:flex-row gap-3">
                                  {att.fileType === 'link' ? (
                                    <>
                                      <input type="text" value={att.fileName} onChange={e => { const newA = [...form.attachments]; newA[gi].files[fi].fileName = e.target.value; setForm({ ...form, attachments: newA }); }} placeholder="عنوان الرابط" className={`${inputCls} !py-2 flex-1 md:w-1/3`} />
                                      <input type="url" value={att.fileUrl} onChange={e => { const newA = [...form.attachments]; newA[gi].files[fi].fileUrl = e.target.value; setForm({ ...form, attachments: newA }); }} placeholder="https://..." className={`${inputCls} !py-2 flex-[2] text-left`} dir="ltr" />
                                    </>
                                  ) : (
                                    <div className="flex flex-col justify-center overflow-hidden">
                                      <p className="text-md font-bold truncate text-slate-800 dark:text-zinc-200" dir="ltr">
                                        {att.fileName}
                                      </p>
                                      {att.fileSize && <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-500 mt-0.5">{att.fileSize}</span>}
                                    </div>
                                  )}
                                </div>

                                <button type="button" onClick={() => { const newA = [...form.attachments]; newA[gi].files.splice(fi, 1); setForm({ ...form, attachments: newA }); }} className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/30 flex items-center justify-center shrink-0 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            {(!group.files || group.files.length === 0) && <div className="text-md font-medium text-slate-400 dark:text-zinc-600 text-center py-4 bg-slate-50/50 dark:bg-white/5 rounded-2xl border border-dashed border-slate-200 dark:border-white/10">التصنيف فارغ. ابدأ بإضافة الروابط أو الملفات.</div>}
                          </div>
                        </div>
                      ))}

                      {(!form.attachments || form.attachments.length === 0) && (
                        <div className="text-base font-bold text-slate-500 dark:text-zinc-500 text-center py-10 bg-slate-50 dark:bg-white/5 border border-dashed border-slate-200 dark:border-white/10 rounded-3xl">
                          لا توجد ملحقات لهذه الدورة بعد. اضغط على الزر أعلاه للبدء.
                        </div>
                      )}
                    </div>
                  </div>
                </div>)}

                {/* TAB: CURRICULUM ───────────────── */}
                {activeTab === 'curriculum' && (<div className="space-y-8 animate-in fade-in duration-300">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <label className="text-lg font-bold text-slate-900 dark:text-white">المنهج والدروس</label>
                      <p className="text-md text-slate-500 dark:text-zinc-500 mt-1">قسم الدورة إلى وحدات ودروس بشكل منطقي. (يمكنك إضافة وحدات ودروس من الشريط السفلي)</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {form.curriculum.map((unit, ui) => (
                      <div key={ui}
                        draggable={true}
                        onDragStart={(e) => {
                          e.stopPropagation();
                          e.dataTransfer.setData('unitIdx', ui.toString());
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!e.dataTransfer.types.includes('lessonidx')) setDragOverUnitIdx(ui);
                        }}
                        onDragLeave={() => setDragOverUnitIdx(null)}
                        onDragEnd={() => setDragOverUnitIdx(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverUnitIdx(null);
                          const fromUi = parseInt(e.dataTransfer.getData('unitIdx'));
                          const fromLi = e.dataTransfer.getData('lessonIdx');
                          if (fromLi) return; // Prevent dropping a lesson onto unit level
                          if (!isNaN(fromUi) && fromUi !== ui) moveUnit(fromUi, ui);
                        }}
                        className={`bg-slate-50/80 dark:bg-[#111] border rounded-2xl p-5 shadow-sm relative group/unit transition-all ${dragOverUnitIdx === ui
                          ? 'border-primary-500 ring-2 ring-primary-500/50 scale-[1.01] bg-primary-50 dark:bg-primary-500/10 z-10'
                          : expandedUnitIdx === ui ? 'border-primary-500/50 shadow-primary-500/10' : 'border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
                          }`}>

                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => {
                          setExpandedUnitIdx(expandedUnitIdx === ui ? -1 : ui);
                          setExpandedLessonIdx(expandedUnitIdx === ui ? expandedLessonIdx : -1);
                        }}>
                          <div className="cursor-grab active:cursor-grabbing w-10 h-10 bg-slate-200/50 dark:bg-white/5 text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 rounded-xl flex items-center justify-center shrink-0 transition-colors" title="سحب لنقل الوحدة" onClick={e => e.stopPropagation()}>
                            <GripVertical className="w-5 h-5 pointer-events-none" />
                          </div>
                          <div className="w-10 h-10 rounded-xl bg-primary-500 text-white text-base font-black flex items-center justify-center shrink-0 shadow-lg shadow-primary-500/20">{ui + 1}</div>
                          <input type="text" value={unit.title} onChange={e => updateUnitTitle(ui, e.target.value)} onClick={e => e.stopPropagation()}
                            placeholder={`عنوان الوحدة ${ui + 1}`} className={`${inputCls} !text-lg !font-bold !py-2.5 flex-1`} />

                          <div className="w-10 h-10 flex items-center justify-center text-slate-400 shrink-0">
                            <ChevronDown className={`w-6 h-6 transition-transform duration-300 ${expandedUnitIdx === ui ? 'rotate-180 text-primary-500' : ''}`} />
                          </div>

                          {form.curriculum.length > 1 && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); removeUnit(ui); }} className="w-10 h-10 bg-white dark:bg-zinc-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 border border-slate-200 dark:border-white/5 rounded-xl flex items-center justify-center shrink-0 shadow-sm transition-colors relative z-10">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${expandedUnitIdx === ui ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                          <div className="overflow-hidden">
                            <div className="space-y-3 mr-4 pr-10 pt-5 mt-2 border-r-2 border-slate-200 dark:border-white/5 relative">
                              <div className="absolute top-0 -right-[2px] w-[2px] h-8 bg-gradient-to-b from-primary-500 to-transparent"></div>

                              {unit.lessons.map((lesson, li) => {
                                const lessonType = lesson.type || 'video';
                                const lsTheme = getLessonTheme(lessonType);
                                const isDragTarget = dragOverLesson?.ui === ui && dragOverLesson?.li === li;
                                return (
                                  <div key={li}
                                    draggable={true}
                                    onDragStart={(e) => {
                                      e.stopPropagation();
                                      e.dataTransfer.setData('lessonIdx', li.toString());
                                      e.dataTransfer.setData('unitIdx', ui.toString());
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (e.dataTransfer.types.includes('lessonidx')) setDragOverLesson({ ui, li });
                                    }}
                                    onDragLeave={() => setDragOverLesson(null)}
                                    onDragEnd={() => setDragOverLesson(null)}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverLesson(null);
                                      const fromLi = parseInt(e.dataTransfer.getData('lessonIdx'));
                                      const fromUi = parseInt(e.dataTransfer.getData('unitIdx'));
                                      if (!isNaN(fromLi) && !isNaN(fromUi) && fromUi === ui && fromLi !== li) {
                                        moveLesson(ui, fromLi, li);
                                      }
                                    }}
                                    className={`rounded-2xl p-4 shadow-sm relative group/lesson transition-all duration-200 outline-none ${isDragTarget
                                      ? 'border-emerald-500 border ring-2 ring-emerald-500/40 scale-[1.01] bg-emerald-50 dark:bg-emerald-500/10 z-10'
                                      : expandedLessonIdx === li
                                        ? `border-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] ${lsTheme.containerExpanded}`
                                        : 'bg-white dark:bg-zinc-900/50 border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'
                                      }`}>

                                    {/* Header row: type selector + title + delete */}
                                    <div className="flex flex-wrap md:flex-nowrap items-center gap-3 cursor-pointer pb-2" onClick={() => setExpandedLessonIdx(expandedLessonIdx === li ? -1 : li)}>
                                      <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 dark:text-zinc-600 dark:hover:text-zinc-400 p-1.5 -ml-1.5 rounded-lg transition-colors" title="سحب لنقل الدرس ضمن الوحدة" onClick={e => e.stopPropagation()}>
                                        <GripVertical className="w-4 h-4 pointer-events-none" />
                                      </div>
                                      <div className={`w-8 h-8 rounded-lg text-md font-black flex items-center justify-center shrink-0 border shadow-sm transition-colors ${lsTheme.numberBox}`}>{li + 1}</div>
                                      <div className="w-6 h-6 flex items-center justify-center text-slate-400 shrink-0">
                                        <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${expandedLessonIdx === li ? 'rotate-180' : ''}`} />
                                      </div>
                                      {/* Type Toggle */}
                                      <div className="flex gap-1 bg-slate-100 dark:bg-black/40 p-1 rounded-xl flex-wrap shrink-0" onClick={e => e.stopPropagation()}>
                                        {[['video', '🎬', 'فيديو'], ['text', '📄', 'مقال'], ['quiz', '✏️', 'اختبار'], ['assignment', '📝', 'تكليف'], ['final_project', '🎓', 'مشروع']].map(([t, emoji, label]) => {
                                          const tTheme = getLessonTheme(t);
                                          return (
                                            <button key={t} type="button"
                                              onClick={() => updateLesson(ui, li, 'type', t)}
                                              title={label}
                                              className={`px-3 py-1.5 rounded-lg text-md font-bold flex items-center gap-1.5 transition-all ${lessonType === t
                                                ? tTheme.activeBtn
                                                : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300 hover:bg-white/50 dark:hover:bg-white/5'
                                                }`}>
                                              <span>{emoji}</span> <span className="hidden sm:inline">{label}</span>
                                            </button>
                                          )
                                        })}
                                      </div>
                                      <input type="text" value={lesson.title} onChange={e => updateLesson(ui, li, 'title', e.target.value)} onClick={e => e.stopPropagation()}
                                        placeholder="عنوان الدرس" className={`${inputCls} flex-1 !font-bold`} />

                                      {/* Access level toggle */}
                                      <button type="button" onClick={(e) => { e.stopPropagation(); updateLesson(ui, li, 'accessLevel', lesson.accessLevel === 'public' ? 'subscriber' : 'public'); }}
                                        className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-md font-bold border transition-all shrink-0 ${lesson.accessLevel === 'public'
                                          ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 dark:hover:bg-emerald-500/20'
                                          : 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20 dark:hover:bg-amber-500/20'
                                          }`}>
                                        {lesson.accessLevel === 'public' ? <><Globe className="w-4 h-4" /> <span className="hidden sm:inline">عام</span></> : <><Lock className="w-4 h-4" /> <span className="hidden sm:inline">مشتركين</span></>}
                                      </button>

                                      {unit.lessons.length > 1 && (
                                        <button type="button" onClick={(e) => { e.stopPropagation(); removeLesson(ui, li); }} className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/20 flex items-center justify-center shrink-0 transition-colors relative z-10">
                                          <Minus className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>

                                    <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${expandedLessonIdx === li ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                                      <div className="overflow-hidden">
                                        <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-white/5">
                                          <textarea value={lesson.description || ''} onChange={e => updateLesson(ui, li, 'description', e.target.value)}
                                            placeholder="وصف أو شرح الدرس (اختياري)..." rows={2} className={`${inputCls} w-full resize-y text-md bg-slate-50/50 dark:bg-black/20`} />

                                          {/* ── VIDEO fields */}
                                          {lessonType === 'video' && (<div className="bg-slate-50 dark:bg-black/20 rounded-xl p-3 border border-slate-100 dark:border-white/5 space-y-3">
                                            <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
                                              <div className="space-y-3">
                                                <div className="flex gap-2">
                                                  <input type="text" value={lesson.videoFilename || lesson.videoUrl || ''} onChange={e => {
                                                    const val = e.target.value.trim();
                                                    updateLesson(ui, li, 'videoFilename', val);
                                                    if (val.length === 36 && val.split('-').length === 5) fetchLessonDuration(ui, li, val);
                                                  }}
                                                    placeholder="Bunny Stream Video ID (مثال: f8caa28a-xxxx...)" className={`${inputCls} flex-1`} dir="ltr"
                                                    disabled={lessonUploadingKey === `${ui}-${li}`}
                                                  />

                                                  <input
                                                    type="file"
                                                    id={`lesson-video-${ui}-${li}`}
                                                    accept="video/*"
                                                    className="hidden"
                                                    onChange={e => handleLessonVideoUpload(ui, li, e)}
                                                  />
                                                  <label
                                                    htmlFor={`lesson-video-${ui}-${li}`}
                                                    className={`px-4 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 cursor-pointer border shadow-sm ${lessonUploadingKey === `${ui}-${li}`
                                                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                                      : 'bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 border-primary-500/20 hover:bg-primary-500/20'
                                                      }`}
                                                  >
                                                    {lessonUploadingKey === `${ui}-${li}` ? (
                                                      <Loader2 className="w-5 h-5 animate-spin" />
                                                    ) : (
                                                      <Upload className="w-5 h-5" />
                                                    )}
                                                    <span>رفع درس</span>
                                                  </label>

                                                  <label className={`px-4 py-2.5 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-white/10 rounded-xl text-md font-bold text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-zinc-700 transition-all shrink-0 flex items-center gap-2 cursor-pointer shadow-sm ${fetchingMeta === `${ui}-${li}` ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {fetchingMeta === `${ui}-${li}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} التقاط غلاف
                                                    <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) handleThumbnailUpload(ui, li, e.target.files[0]); }} />
                                                  </label>
                                                </div>

                                                {lessonUploadingKey === `${ui}-${li}` && (
                                                  <div className="w-full h-2 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden border border-slate-200 dark:border-white/10">
                                                    <motion.div
                                                      initial={{ width: 0 }}
                                                      animate={{ width: `${videoUploadProgress}%` }}
                                                      className="h-full bg-primary-500"
                                                    />
                                                  </div>
                                                )}
                                                {lessonUploadingKey === `${ui}-${li}` && (
                                                  <p className="text-xs text-slate-500 dark:text-zinc-400 font-bold text-right px-1">
                                                    جاري رفع فيديو الدرس... {videoUploadProgress}%
                                                  </p>
                                                )}
                                              </div>
                                              {lesson.thumbnailUrl && <img src={lesson.thumbnailUrl} alt="Thumbnail" className="w-12 h-12 rounded-lg object-cover border-2 border-white dark:border-zinc-700 shadow-md shrink-0" />}
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="flex items-center px-3 py-2 bg-white dark:bg-zinc-800 border border-slate-200 dark:border-white/10 rounded-xl">
                                                <span className="text-slate-500 font-bold text-md ml-2">دقيقة</span>
                                                <input type="number" min="0" value={lesson.durationMin} onChange={e => updateLesson(ui, li, 'durationMin', e.target.value)}
                                                  placeholder="00" className="w-10 bg-transparent text-center text-md font-bold text-slate-900 dark:text-white outline-none" dir="ltr" />
                                                <span className="text-slate-300 dark:text-zinc-600 font-bold mx-1">:</span>
                                                <input type="number" min="0" max="59" value={lesson.durationSec} onChange={e => updateLesson(ui, li, 'durationSec', e.target.value)}
                                                  placeholder="00" className="w-10 bg-transparent text-center text-md font-bold text-slate-900 dark:text-white outline-none" dir="ltr" />
                                                <span className="text-slate-500 font-bold text-md mr-2">ثانية</span>
                                              </div>
                                              {fetchingMeta === `dur-${ui}-${li}` && <Loader2 className="w-5 h-5 animate-spin text-primary-500 shrink-0 mr-2" />}
                                            </div>
                                          </div>)}

                                          {/* ── TEXT / ARTICLE fields */}
                                          {lessonType === 'text' && (
                                            <textarea
                                              value={lesson.textContent || ''}
                                              onChange={e => updateLesson(ui, li, 'textContent', e.target.value)}
                                              placeholder="اكتب محتوى المقال هنا... (يدعم HTML البسيط مثل <b>, <ul>, <p>)"
                                              rows={8}
                                              className={`${inputCls} !py-4 resize-y font-mono text-md leading-relaxed bg-slate-50 dark:bg-black/20`}
                                            />
                                          )}

                                          {/* ── ASSIGNMENT / FINAL PROJECT fields */}
                                          {(lessonType === 'assignment' || lessonType === 'final_project') && (
                                            <div className="bg-slate-50 dark:bg-black/20 rounded-xl p-4 border border-slate-100 dark:border-white/5 space-y-4">
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                  <label className="block text-md font-bold text-slate-700 dark:text-zinc-300 mb-2">الدرجة القصوى</label>
                                                  <input type="number" min="1" value={lesson.maxScore || 100} onChange={e => updateLesson(ui, li, 'maxScore', parseInt(e.target.value))}
                                                    className={`${inputCls} !py-3 w-full bg-white dark:bg-zinc-800`} />
                                                </div>
                                                <div className="flex items-center gap-3 pt-2 md:pt-8">
                                                  <label className="flex items-center gap-3 text-md font-bold text-slate-700 dark:text-zinc-300 cursor-pointer">
                                                    <input type="checkbox" checked={lesson.isSkippable || false} onChange={e => updateLesson(ui, li, 'isSkippable', e.target.checked)} className="accent-primary-600 w-5 h-5 rounded" />
                                                    السماح للطالب بتخطي التكليف
                                                  </label>
                                                </div>
                                              </div>
                                            </div>
                                          )}

                                          {/* ── QUIZ fields */}
                                          {lessonType === 'quiz' && (() => {
                                            const qd = lesson.quizData || { questions: [], isMandatory: false, allowRetakes: true, cooldownHours: 0, passingScore: 60 };
                                            const updateQD = (patch) => {
                                              const cur = [...form.curriculum];
                                              cur[ui].lessons[li].quizData = { ...qd, ...patch };
                                              setForm({ ...form, curriculum: cur });
                                            };
                                            const updateQ = (qi, patch) => {
                                              const qs = [...(qd.questions || [])];
                                              qs[qi] = { ...qs[qi], ...patch };
                                              updateQD({ questions: qs });
                                            };
                                            const updateOpt = (qi, oi, val) => {
                                              const qs = [...(qd.questions || [])];
                                              const opts = [...(qs[qi].options || [])];
                                              opts[oi] = val;
                                              qs[qi] = { ...qs[qi], options: opts };
                                              updateQD({ questions: qs });
                                            };
                                            return (
                                              <div className="space-y-5 bg-slate-50 dark:bg-black/20 p-4 rounded-xl border border-slate-100 dark:border-white/5">
                                                {/* Quiz Settings */}
                                                <div className="flex flex-wrap gap-4 items-center">
                                                  <label className="flex items-center gap-2 text-md font-bold text-slate-700 dark:text-zinc-300 cursor-pointer bg-white dark:bg-zinc-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30 transition-colors">
                                                    <input type="checkbox" checked={qd.isMandatory} onChange={e => updateQD({ isMandatory: e.target.checked })} className="accent-primary-600 w-4 h-4" />
                                                    إلزامي للتقدم
                                                  </label>
                                                  <label className="flex items-center gap-2 text-md font-bold text-slate-700 dark:text-zinc-300 cursor-pointer bg-white dark:bg-zinc-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-500/30 transition-colors">
                                                    <input type="checkbox" checked={qd.allowRetakes} onChange={e => updateQD({ allowRetakes: e.target.checked })} className="accent-primary-600 w-4 h-4" />
                                                    السماح بالإعادة
                                                  </label>
                                                  <div className="flex items-center bg-white dark:bg-zinc-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-white/10">
                                                    <span className="text-md font-bold text-slate-500 ml-2">مهلة الإعادة:</span>
                                                    <input type="number" min="0" max="72" value={qd.cooldownHours} onChange={e => updateQD({ cooldownHours: parseInt(e.target.value) || 0 })} className="w-12 text-center text-md font-bold bg-transparent outline-none text-slate-900 dark:text-white" dir="ltr" />
                                                    <span className="text-md font-bold text-slate-400 mr-1">ساعة</span>
                                                  </div>
                                                  <div className="flex items-center bg-white dark:bg-zinc-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-white/10">
                                                    <span className="text-md font-bold text-slate-500 ml-2">النجاح:</span>
                                                    <input type="number" min="1" max="100" value={qd.passingScore || 60} onChange={e => updateQD({ passingScore: parseInt(e.target.value) || 60 })} className="w-12 text-center text-md font-bold bg-transparent outline-none text-slate-900 dark:text-white" dir="ltr" />
                                                    <span className="text-md font-bold text-slate-400 mr-1">%</span>
                                                  </div>
                                                </div>

                                                {/* Questions */}
                                                <div className="space-y-4">
                                                  {(qd.questions || []).map((q, qi) => (
                                                    <div key={qi} className="bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-white/10 rounded-2xl p-4 space-y-4 shadow-sm">
                                                      <div className="flex items-center gap-3">
                                                        <span className="w-8 h-8 rounded-xl bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 text-md font-black flex items-center justify-center shrink-0">{qi + 1}</span>
                                                        <input type="text" value={q.question} onChange={e => updateQ(qi, { question: e.target.value })} placeholder="اكتب نص السؤال هنا..." className={`${inputCls} flex-1 !font-bold`} />
                                                        <button type="button" onClick={() => { const qs = [...qd.questions]; qs.splice(qi, 1); updateQD({ questions: qs }); }} className="w-10 h-10 bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-xl flex items-center justify-center shrink-0 transition-colors"><Trash2 className="w-4 h-4" /></button>
                                                      </div>
                                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mr-11">
                                                        {(q.options || []).map((opt, oi) => (
                                                          <div key={oi} className={`flex items-center gap-3 p-2 rounded-xl border transition-colors ${q.correctIndex === oi ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30' : 'bg-slate-50 border-slate-200 dark:bg-black/20 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10'}`}>
                                                            <input type="radio" name={`q-${ui}-${li}-${qi}`} checked={q.correctIndex === oi} onChange={() => updateQ(qi, { correctIndex: oi })} className="accent-emerald-500 w-5 h-5 shrink-0 cursor-pointer" title="حدد هذه الإجابة كإجابة صحيحة" />
                                                            <input type="text" value={opt} onChange={e => updateOpt(qi, oi, e.target.value)} placeholder={`الخيار ${oi + 1}`} className="flex-1 text-md font-bold bg-transparent outline-none text-slate-700 dark:text-zinc-200 placeholder:text-slate-400" />
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>

                                                <button type="button" onClick={() => updateQD({ questions: [...(qd.questions || []), blankQuestion()] })}
                                                  className="w-full py-3.5 border-2 border-dashed border-primary-200 dark:border-primary-900/50 rounded-2xl text-md font-bold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all flex items-center justify-center gap-2">
                                                  <Plus className="w-5 h-5" /> إضافة سؤال جديد
                                                </button>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals preview */}
                  {(() => {
                    const t = calcTotals(form.curriculum); return (
                      <div className="bg-gradient-to-r from-primary-50 to-emerald-50 dark:from-primary-900/20 dark:to-emerald-900/10 border border-primary-100 dark:border-primary-900/30 rounded-3xl p-6 flex items-center justify-around text-center shadow-inner mt-8">
                        <div className="space-y-1"><p className="text-3xl font-black text-primary-700 dark:text-primary-300">{t.totalUnits}</p><p className="text-md text-primary-600/80 font-bold tracking-wide">وحدة تعليمية</p></div>
                        <div className="w-px h-12 bg-primary-200 dark:bg-primary-800/50"></div>
                        <div className="space-y-1"><p className="text-3xl font-black text-primary-700 dark:text-primary-300">{t.totalLessons}</p><p className="text-md text-primary-600/80 font-bold tracking-wide">درس متكامل</p></div>
                        <div className="w-px h-12 bg-primary-200 dark:bg-primary-800/50"></div>
                        <div className="space-y-1"><p className="text-3xl font-black text-primary-700 dark:text-primary-300" dir="ltr">{t.durationLabel}</p><p className="text-md text-primary-600/80 font-bold tracking-wide">إجمالي المدة</p></div>
                      </div>
                    );
                  })()}
                </div>)}

                {/* Submit Buttons */}
                <div className="sticky bottom-0 -mx-6 md:-mx-10 -mb-6 mt-8 p-6 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-3xl border-t border-slate-100 dark:border-white/10 flex gap-4 z-10 rounded-b-3xl">
                  {activeTab === 'curriculum' && (
                    <>
                      <button type="button" onClick={addUnit} className="px-5 py-4 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-white rounded-2xl font-bold transition-all flex-shrink-0 flex items-center gap-2">
                        <Plus className="w-5 h-5" /> <span className="hidden sm:inline">إضافة وحدة</span>
                      </button>
                      <button type="button" onClick={() => {
                        if (expandedUnitIdx >= 0 && expandedUnitIdx < form.curriculum.length) addLesson(expandedUnitIdx);
                        else if (form.curriculum.length > 0) addLesson(form.curriculum.length - 1);
                      }}
                        disabled={form.curriculum.length === 0}
                        className="px-5 py-4 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-white rounded-2xl font-bold transition-all flex-shrink-0 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        <Plus className="w-5 h-5" />
                        <span className="hidden sm:inline">إضافة درس {(form.curriculum.length > 0) ? `(للوحدة ${expandedUnitIdx >= 0 ? expandedUnitIdx + 1 : form.curriculum.length})` : ''}</span>
                      </button>
                    </>
                  )}
                  <div className="flex-1"></div>
                  <Link to="/admin/courses"
                    className="px-6 py-4 bg-slate-100 hover:bg-slate-200 dark:bg-transparent dark:hover:bg-white/5 text-slate-500 dark:text-zinc-400 rounded-2xl font-bold transition-all flex-shrink-0 flex items-center">
                    إلغاء
                  </Link>
                  <button type="submit" disabled={isSaving}
                    className="px-8 py-4 bg-gradient-to-l from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white rounded-2xl font-bold text-lg transition-all shadow-[0_0_40px_rgba(var(--primary-500),0.3)] disabled:opacity-60 flex justify-center items-center gap-3 group relative overflow-hidden shrink-0">
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                    {isSaving ? <Loader2 className="w-6 h-6 animate-spin relative z-10" /> : <span className="relative z-10">{editingCourse ? 'حفظ التعديلات ونشر' : 'إنشاء الدورة الآن'}</span>}
                  </button>
                </div>
              </form>
            </div>
    </div>
  )
}
